import { useRef, ReactNode, MouseEvent } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

type MagneticButtonProps = {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
  /** How far (px) the element shifts toward the cursor at maximum. */
  strength?: number;
  ariaLabel?: string;
};

/**
 * Magnetic hover wrapper — shifts the contents a small distance toward the
 * cursor while it's over the element. Uses spring-damped motion values so the
 * effect feels physical, not robotic. All movement is GPU transform; no
 * layout work happens.
 *
 * Disabled on touch devices via the (hover: none) media query — touch users
 * never get to "hover" so there's no benefit to the cost.
 */
export default function MagneticButton({
  children,
  href,
  onClick,
  className,
  strength = 18,
  ariaLabel,
}: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springX = useSpring(x, { stiffness: 220, damping: 18, mass: 0.6 });
  const springY = useSpring(y, { stiffness: 220, damping: 18, mass: 0.6 });

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(hover: none)').matches) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    // Clamp to ±strength so the element never flies off
    const clamp = (v: number, max: number) =>
      Math.max(-max, Math.min(max, v));
    x.set(clamp(relX * 0.35, strength));
    y.set(clamp(relY * 0.35, strength));
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const inner = (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: springX, y: springY }}
      className={'inline-block transform-gpu ' + (className ?? '')}
      aria-label={ariaLabel}
    >
      {children}
    </motion.div>
  );

  if (href) {
    return (
      <a href={href} className="inline-block" aria-label={ariaLabel}>
        {inner}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-block"
        aria-label={ariaLabel}
      >
        {inner}
      </button>
    );
  }

  return inner;
}
